import { useState } from "react";
import { Alert, Button, Checkbox, Empty } from "antd";

import Loader from "@/components/UI/Loader";
import {
  isNotFoundError,
  useSpirometryCurvesQuery,
} from "@/hooks/useWaveformQueries";
import type { SpirometryEffortCurve } from "../types";
import { XYLinePlot } from "../shared/XYLinePlot";
import type { XYSeries } from "../shared/XYLinePlot";
import { downsampleMinMax } from "../utils/downsample";

const EFFORT_COLORS = [
  "#1890ff",
  "#52c41a",
  "#fa8c16",
  "#eb2f96",
  "#722ed1",
  "#13c2c2",
  "#f5222d",
  "#a0d911",
];

const ML_PER_L = 1000;
const MS_PER_SECOND = 1000;
const VOLUME_TIME_MAX_POINTS = 1000;
const EMPTY_DESCRIPTION = "No spirometry waveform available";

export interface SpirometryViewerProps {
  transmittalNumber: number;
}

const volumeExcursion = (effort: SpirometryEffortCurve) => {
  if (!effort.volume_ml.length) return 0;
  let min = Infinity;
  let max = -Infinity;
  for (const volume of effort.volume_ml) {
    if (volume < min) min = volume;
    if (volume > max) max = volume;
  }
  return max - min;
};

/** The effort with the largest volume excursion (~best FVC) is emphasized. */
const findEmphasizedEffortNumber = (
  efforts: SpirometryEffortCurve[],
): number | null => {
  let bestEffort: number | null = null;
  let bestExcursion = -Infinity;
  for (const effort of efforts) {
    const excursion = volumeExcursion(effort);
    if (excursion > bestExcursion) {
      bestExcursion = excursion;
      bestEffort = effort.effort_number;
    }
  }
  return bestEffort;
};

const toFlowVolumeSeries = (
  effort: SpirometryEffortCurve,
  color: string,
  emphasis: boolean,
): XYSeries => ({
  key: `fv-${effort.effort_number}`,
  label: `Effort ${effort.effort_number}`,
  color,
  emphasis,
  // Sample order — the flow-volume loop is parametric, not a function of x.
  points: effort.volume_ml.map((volume, i) => ({
    x: volume / ML_PER_L,
    y: effort.flow_ml[i] / ML_PER_L,
  })),
});

const toVolumeTimeSeries = (
  effort: SpirometryEffortCurve,
  color: string,
  emphasis: boolean,
): XYSeries => {
  const { xs, ys } = downsampleMinMax(
    effort.time_ms,
    effort.volume_ml,
    VOLUME_TIME_MAX_POINTS,
  );
  return {
    key: `vt-${effort.effort_number}`,
    label: `Effort ${effort.effort_number}`,
    color,
    emphasis,
    points: xs.map((timeMs, i) => ({
      x: timeMs / MS_PER_SECOND,
      y: ys[i] / ML_PER_L,
    })),
  };
};

export const SpirometryViewer = ({
  transmittalNumber,
}: SpirometryViewerProps) => {
  const [hiddenEfforts, setHiddenEfforts] = useState<number[]>([]);

  const { data, isLoading, error, refetch } = useSpirometryCurvesQuery({
    transmittalNumber,
  });

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <Loader noMargin />
      </div>
    );
  }

  if (error && isNotFoundError(error)) {
    return <Empty description={EMPTY_DESCRIPTION} />;
  }

  if (error) {
    return (
      <Alert
        type="error"
        showIcon
        message="Waveform service unavailable"
        description="The spirometry curves could not be loaded. The time-series database may be temporarily unreachable."
        action={
          <Button size="small" onClick={() => refetch()}>
            Retry
          </Button>
        }
      />
    );
  }

  const efforts = data?.efforts ?? [];
  if (!efforts.length) {
    return <Empty description={EMPTY_DESCRIPTION} />;
  }

  const emphasizedEffort = findEmphasizedEffortNumber(efforts);
  const colorForIndex = (index: number) =>
    EFFORT_COLORS[index % EFFORT_COLORS.length];

  const isVisible = (effort: SpirometryEffortCurve) =>
    !hiddenEfforts.includes(effort.effort_number);

  const flowVolumeSeries = efforts
    .map((effort, index) =>
      isVisible(effort)
        ? toFlowVolumeSeries(
            effort,
            colorForIndex(index),
            effort.effort_number === emphasizedEffort,
          )
        : null,
    )
    .filter((s): s is XYSeries => s !== null);

  const volumeTimeSeries = efforts
    .map((effort, index) =>
      isVisible(effort)
        ? toVolumeTimeSeries(
            effort,
            colorForIndex(index),
            effort.effort_number === emphasizedEffort,
          )
        : null,
    )
    .filter((s): s is XYSeries => s !== null);

  const toggleEffort = (effortNumber: number, checked: boolean) => {
    setHiddenEfforts((prev) =>
      checked
        ? prev.filter((n) => n !== effortNumber)
        : [...prev, effortNumber],
    );
  };

  return (
    <div>
      <div className="flex flex-wrap items-center gap-x-5 gap-y-1 mb-3">
        {efforts.map((effort, index) => (
          <Checkbox
            key={effort.effort_number}
            checked={isVisible(effort)}
            onChange={(e) =>
              toggleEffort(effort.effort_number, e.target.checked)
            }
          >
            <span className="inline-flex items-center gap-1.5">
              <span
                className="inline-block w-3 h-[3px] rounded"
                style={{ background: colorForIndex(index) }}
              />
              Effort {effort.effort_number}
              {effort.effort_number === emphasizedEffort && (
                <span className="text-xs text-gray-400">(best)</span>
              )}
            </span>
          </Checkbox>
        ))}
      </div>

      <div className="flex flex-wrap gap-6">
        <div className="flex-[3] min-w-[320px]">
          <p className="text-xs font-semibold text-gray-600 mb-1">
            Flow–volume loop
          </p>
          <XYLinePlot
            series={flowVolumeSeries}
            xLabel="Volume (L)"
            yLabel="Flow (L/s)"
          />
        </div>
        <div className="flex-[2] min-w-[280px]">
          <p className="text-xs font-semibold text-gray-600 mb-1">
            Volume–time
          </p>
          <XYLinePlot
            series={volumeTimeSeries}
            xLabel="Time (s)"
            yLabel="Volume (L)"
          />
        </div>
      </div>
    </div>
  );
};
